// SNIGJ - Legal Documents Page (Mandats, Convocations, Ordres)
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  FileText, Plus, Search, Filter, Eye, Edit, Printer, Send,
  CheckCircle, XCircle, Clock, AlertTriangle, Scale, User,
  Calendar, MapPin, FileSignature, Gavel, Target, Loader2,
  Download, Stamp, ArrowRight
} from 'lucide-react';

// Document types with translations
const DOCUMENT_TYPES = [
  { value: 'MANDAT_AMENER', label: { fr: 'Mandat d\'amener', en: 'Summons Warrant' }, icon: User, color: 'bg-blue-500' },
  { value: 'MANDAT_ARRET', label: { fr: 'Mandat d\'arrêt', en: 'Arrest Warrant' }, icon: Target, color: 'bg-red-500' },
  { value: 'MANDAT_DEPOT', label: { fr: 'Mandat de dépôt', en: 'Custody Warrant' }, icon: Scale, color: 'bg-orange-500' },
  { value: 'ORDRE_PERQUISITION', label: { fr: 'Ordre de perquisition', en: 'Search Order' }, icon: Search, color: 'bg-purple-500' },
  { value: 'ORDONNANCE_PLACEMENT', label: { fr: 'Ordonnance de placement', en: 'Placement Order' }, icon: Gavel, color: 'bg-indigo-500' },
  { value: 'CONVOCATION', label: { fr: 'Convocation', en: 'Summons' }, icon: Calendar, color: 'bg-green-500' },
  { value: 'CITATION_DIRECTE', label: { fr: 'Citation directe', en: 'Direct Citation' }, icon: FileText, color: 'bg-yellow-500' },
  { value: 'REQUISITOIRE', label: { fr: 'Réquisitoire', en: 'Requisition' }, icon: FileSignature, color: 'bg-teal-500' },
];

// Status badges
const STATUS_CONFIG = {
  DRAFT: { label: { fr: 'Brouillon', en: 'Draft' }, color: 'bg-gray-500' },
  SIGNED: { label: { fr: 'Signé', en: 'Signed' }, color: 'bg-blue-500' },
  SENT: { label: { fr: 'Envoyé', en: 'Sent' }, color: 'bg-yellow-500' },
  EXECUTED: { label: { fr: 'Exécuté', en: 'Executed' }, color: 'bg-green-500' },
  CANCELLED: { label: { fr: 'Annulé', en: 'Cancelled' }, color: 'bg-red-500' },
  EXPIRED: { label: { fr: 'Expiré', en: 'Expired' }, color: 'bg-orange-500' },
};

const URGENCY_CONFIG = {
  LOW: { label: { fr: 'Faible', en: 'Low' }, color: 'text-gray-500' },
  NORMAL: { label: { fr: 'Normal', en: 'Normal' }, color: 'text-blue-500' },
  HIGH: { label: { fr: 'Haute', en: 'High' }, color: 'text-orange-500' },
  URGENT: { label: { fr: 'Urgente', en: 'Urgent' }, color: 'text-red-500' },
};

const LegalDocumentsPage = () => {
  const { language, user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    drafts: 0,
    pending: 0,
    executed: 0
  });

  // Form data for new document
  const [formData, setFormData] = useState({
    document_type: '',
    title: '',
    description: '',
    case_id: '',
    target_name: '',
    target_national_id: '',
    target_address: '',
    target_description: '',
    execution_location: '',
    execution_date: '',
    execution_deadline: '',
    issuing_authority: user?.jurisdiction || 'Tribunal de Grande Instance',
    issuing_magistrate: user?.name || '',
    urgency: 'NORMAL',
    legal_basis: '',
    motives: '',
    instructions: ''
  });

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (typeFilter && typeFilter !== 'all') params.append('document_type', typeFilter);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await api.get(`/legal-documents?${params.toString()}`);
      const docs = response.data || [];
      setDocuments(docs);
      
      // Calculate stats
      setStats({
        total: docs.length,
        drafts: docs.filter(d => d.status === 'DRAFT').length,
        pending: docs.filter(d => ['SIGNED', 'SENT'].includes(d.status)).length,
        executed: docs.filter(d => d.status === 'EXECUTED').length
      });
    } catch (error) {
      console.error('Failed to load legal documents:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, typeFilter, statusFilter, language]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleCreateDocument = async () => {
    if (!formData.document_type || !formData.title || !formData.target_name) {
      toast.error(language === 'fr' ? 'Veuillez remplir les champs obligatoires' : 'Please fill required fields');
      return;
    }

    try {
      setSaving(true);
      await api.post('/legal-documents', formData);
      toast.success(language === 'fr' ? 'Document créé avec succès' : 'Document created successfully');
      setShowCreateModal(false);
      resetForm();
      loadDocuments();
    } catch (error) {
      console.error('Failed to create document:', error);
      toast.error(language === 'fr' ? 'Erreur lors de la création' : 'Failed to create document');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async (docId, newStatus) => {
    try {
      await api.put(`/legal-documents/${docId}`, { status: newStatus });
      toast.success(language === 'fr' ? 'Statut mis à jour' : 'Status updated');
      loadDocuments();
      if (selectedDocument?.legal_document_id === docId) {
        setSelectedDocument(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de la mise à jour' : 'Failed to update');
    }
  };

  const resetForm = () => {
    setFormData({
      document_type: '',
      title: '',
      description: '',
      case_id: '',
      target_name: '',
      target_national_id: '',
      target_address: '',
      target_description: '',
      execution_location: '',
      execution_date: '',
      execution_deadline: '',
      issuing_authority: user?.jurisdiction || 'Tribunal de Grande Instance',
      issuing_magistrate: user?.name || '',
      urgency: 'NORMAL',
      legal_basis: '',
      motives: '',
      instructions: ''
    });
  };

  const getDocumentTypeInfo = (type) => {
    return DOCUMENT_TYPES.find(t => t.value === type) || DOCUMENT_TYPES[0];
  };

  const filteredByTab = documents.filter(doc => {
    if (activeTab === 'all') return true;
    if (activeTab === 'warrants') return ['MANDAT_AMENER', 'MANDAT_ARRET', 'MANDAT_DEPOT'].includes(doc.document_type);
    if (activeTab === 'summons') return ['CONVOCATION', 'CITATION_DIRECTE'].includes(doc.document_type);
    if (activeTab === 'orders') return ['ORDRE_PERQUISITION', 'ORDONNANCE_PLACEMENT', 'REQUISITOIRE'].includes(doc.document_type);
    return true;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="legal-documents-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
              <Scale className="w-8 h-8 text-primary" />
              {language === 'fr' ? 'Documents Juridiques' : 'Legal Documents'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' 
                ? 'Mandats, convocations, ordres de perquisition'
                : 'Warrants, summons, search orders'}
            </p>
          </div>
          <Button onClick={() => setShowCreateModal(true)} data-testid="create-document-btn">
            <Plus className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'Nouveau document' : 'New Document'}
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Total' : 'Total'}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-900/30 flex items-center justify-center">
                <Edit className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.drafts}</p>
                <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Brouillons' : 'Drafts'}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.pending}</p>
                <p className="text-xs text-muted-foreground">{language === 'fr' ? 'En attente' : 'Pending'}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.executed}</p>
                <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Exécutés' : 'Executed'}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={language === 'fr' ? 'Rechercher...' : 'Search...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder={language === 'fr' ? 'Type de document' : 'Document type'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Tous les types' : 'All types'}</SelectItem>
                  {DOCUMENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label[language] || type.label.fr}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder={language === 'fr' ? 'Statut' : 'Status'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label[language] || config.label.fr}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tabs & Documents List */}
        <Card>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <CardHeader className="pb-2">
              <TabsList className="grid grid-cols-4 w-full max-w-md">
                <TabsTrigger value="all" className="text-xs sm:text-sm">
                  {language === 'fr' ? 'Tous' : 'All'}
                </TabsTrigger>
                <TabsTrigger value="warrants" className="text-xs sm:text-sm">
                  {language === 'fr' ? 'Mandats' : 'Warrants'}
                </TabsTrigger>
                <TabsTrigger value="summons" className="text-xs sm:text-sm">
                  {language === 'fr' ? 'Convoc.' : 'Summons'}
                </TabsTrigger>
                <TabsTrigger value="orders" className="text-xs sm:text-sm">
                  {language === 'fr' ? 'Ordres' : 'Orders'}
                </TabsTrigger>
              </TabsList>
            </CardHeader>

            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : filteredByTab.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">
                    {language === 'fr' ? 'Aucun document trouvé' : 'No documents found'}
                  </p>
                  <Button variant="outline" className="mt-4" onClick={() => setShowCreateModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    {language === 'fr' ? 'Créer un document' : 'Create document'}
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{language === 'fr' ? 'Numéro' : 'Number'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Type' : 'Type'}</TableHead>
                        <TableHead className="hidden sm:table-cell">{language === 'fr' ? 'Titre' : 'Title'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Cible' : 'Target'}</TableHead>
                        <TableHead className="hidden md:table-cell">{language === 'fr' ? 'Urgence' : 'Urgency'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Statut' : 'Status'}</TableHead>
                        <TableHead className="text-right">{language === 'fr' ? 'Actions' : 'Actions'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredByTab.map((doc) => {
                        const typeInfo = getDocumentTypeInfo(doc.document_type);
                        const statusConfig = STATUS_CONFIG[doc.status] || STATUS_CONFIG.DRAFT;
                        const urgencyConfig = URGENCY_CONFIG[doc.urgency] || URGENCY_CONFIG.NORMAL;
                        
                        return (
                          <TableRow key={doc.legal_document_id} className="cursor-pointer hover:bg-muted/50">
                            <TableCell className="font-mono text-xs">
                              {doc.document_number?.substring(0, 15) || 'N/A'}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className={`w-6 h-6 rounded ${typeInfo.color} flex items-center justify-center`}>
                                  <typeInfo.icon className="w-3 h-3 text-white" />
                                </div>
                                <span className="text-xs hidden lg:inline">
                                  {typeInfo.label[language] || typeInfo.label.fr}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell max-w-[150px] truncate">
                              {doc.title}
                            </TableCell>
                            <TableCell className="font-medium">
                              {doc.target_name?.substring(0, 20)}
                            </TableCell>
                            <TableCell className="hidden md:table-cell">
                              <span className={`text-xs font-medium ${urgencyConfig.color}`}>
                                {urgencyConfig.label[language] || urgencyConfig.label.fr}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge className={`${statusConfig.color} text-white text-xs`}>
                                {statusConfig.label[language] || statusConfig.label.fr}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => {
                                    setSelectedDocument(doc);
                                    setShowDetailModal(true);
                                  }}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <Printer className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Tabs>
        </Card>

        {/* Quick Create Cards - Document Types */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {DOCUMENT_TYPES.slice(0, 4).map((type) => (
            <Card
              key={type.value}
              className="p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => {
                setFormData(prev => ({ ...prev, document_type: type.value }));
                setShowCreateModal(true);
              }}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${type.color} flex items-center justify-center`}>
                  <type.icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{type.label[language] || type.label.fr}</p>
                  <p className="text-xs text-muted-foreground">
                    {language === 'fr' ? 'Créer' : 'Create'} →
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Create Document Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="w-5 h-5" />
              {language === 'fr' ? 'Nouveau Document Juridique' : 'New Legal Document'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? 'Créez un mandat, une convocation ou un ordre'
                : 'Create a warrant, summons or order'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Document Type Selection */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {DOCUMENT_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, document_type: type.value }))}
                  className={`p-3 rounded-lg border text-center transition-all ${
                    formData.document_type === type.value
                      ? 'border-primary bg-primary/10 ring-2 ring-primary'
                      : 'border-muted hover:border-primary/50'
                  }`}
                >
                  <div className={`w-8 h-8 rounded ${type.color} flex items-center justify-center mx-auto mb-1`}>
                    <type.icon className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-xs font-medium truncate">{type.label[language] || type.label.fr}</p>
                </button>
              ))}
            </div>

            <Separator />

            {/* Form Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>{language === 'fr' ? 'Titre du document *' : 'Document title *'}</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder={language === 'fr' ? 'Titre du document' : 'Document title'}
                />
              </div>

              <div className="sm:col-span-2">
                <Label>{language === 'fr' ? 'Nom de la cible *' : 'Target name *'}</Label>
                <Input
                  value={formData.target_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, target_name: e.target.value }))}
                  placeholder={language === 'fr' ? 'Nom complet' : 'Full name'}
                />
              </div>

              <div>
                <Label>{language === 'fr' ? 'Numéro d\'identité' : 'ID Number'}</Label>
                <Input
                  value={formData.target_national_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, target_national_id: e.target.value }))}
                />
              </div>

              <div>
                <Label>{language === 'fr' ? 'Urgence' : 'Urgency'}</Label>
                <Select
                  value={formData.urgency}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, urgency: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(URGENCY_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        {config.label[language] || config.label.fr}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="sm:col-span-2">
                <Label>{language === 'fr' ? 'Adresse de la cible' : 'Target address'}</Label>
                <Input
                  value={formData.target_address}
                  onChange={(e) => setFormData(prev => ({ ...prev, target_address: e.target.value }))}
                />
              </div>

              <div>
                <Label>{language === 'fr' ? 'Lieu d\'exécution' : 'Execution location'}</Label>
                <Input
                  value={formData.execution_location}
                  onChange={(e) => setFormData(prev => ({ ...prev, execution_location: e.target.value }))}
                />
              </div>

              <div>
                <Label>{language === 'fr' ? 'Date limite' : 'Deadline'}</Label>
                <Input
                  type="date"
                  value={formData.execution_deadline}
                  onChange={(e) => setFormData(prev => ({ ...prev, execution_deadline: e.target.value }))}
                />
              </div>

              <div>
                <Label>{language === 'fr' ? 'Autorité émettrice' : 'Issuing authority'}</Label>
                <Input
                  value={formData.issuing_authority}
                  onChange={(e) => setFormData(prev => ({ ...prev, issuing_authority: e.target.value }))}
                />
              </div>

              <div>
                <Label>{language === 'fr' ? 'Magistrat' : 'Magistrate'}</Label>
                <Input
                  value={formData.issuing_magistrate}
                  onChange={(e) => setFormData(prev => ({ ...prev, issuing_magistrate: e.target.value }))}
                />
              </div>

              <div className="sm:col-span-2">
                <Label>{language === 'fr' ? 'Base légale' : 'Legal basis'}</Label>
                <Textarea
                  value={formData.legal_basis}
                  onChange={(e) => setFormData(prev => ({ ...prev, legal_basis: e.target.value }))}
                  placeholder={language === 'fr' ? 'Articles de loi, références...' : 'Law articles, references...'}
                  rows={2}
                />
              </div>

              <div className="sm:col-span-2">
                <Label>{language === 'fr' ? 'Motifs' : 'Motives'}</Label>
                <Textarea
                  value={formData.motives}
                  onChange={(e) => setFormData(prev => ({ ...prev, motives: e.target.value }))}
                  placeholder={language === 'fr' ? 'Raisons justifiant ce document...' : 'Reasons for this document...'}
                  rows={3}
                />
              </div>

              <div className="sm:col-span-2">
                <Label>{language === 'fr' ? 'Instructions spécifiques' : 'Specific instructions'}</Label>
                <Textarea
                  value={formData.instructions}
                  onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
                  placeholder={language === 'fr' ? 'Instructions pour l\'exécution...' : 'Execution instructions...'}
                  rows={2}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateModal(false); resetForm(); }}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button onClick={handleCreateDocument} disabled={saving}>
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {language === 'fr' ? 'Création...' : 'Creating...'}</>
              ) : (
                <><Plus className="w-4 h-4 mr-2" /> {language === 'fr' ? 'Créer' : 'Create'}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedDocument && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  {(() => {
                    const typeInfo = getDocumentTypeInfo(selectedDocument.document_type);
                    return (
                      <div className={`w-10 h-10 rounded-lg ${typeInfo.color} flex items-center justify-center`}>
                        <typeInfo.icon className="w-5 h-5 text-white" />
                      </div>
                    );
                  })()}
                  <div>
                    <DialogTitle>{selectedDocument.title}</DialogTitle>
                    <DialogDescription className="font-mono text-xs">
                      {selectedDocument.document_number}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Status & Actions */}
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={`${STATUS_CONFIG[selectedDocument.status]?.color || 'bg-gray-500'} text-white`}>
                    {STATUS_CONFIG[selectedDocument.status]?.label[language] || selectedDocument.status}
                  </Badge>
                  <Badge variant="outline" className={URGENCY_CONFIG[selectedDocument.urgency]?.color}>
                    {URGENCY_CONFIG[selectedDocument.urgency]?.label[language] || selectedDocument.urgency}
                  </Badge>
                  
                  <div className="flex-1" />
                  
                  {selectedDocument.status === 'DRAFT' && (
                    <Button size="sm" onClick={() => handleUpdateStatus(selectedDocument.legal_document_id, 'SIGNED')}>
                      <Stamp className="w-4 h-4 mr-1" /> {language === 'fr' ? 'Signer' : 'Sign'}
                    </Button>
                  )}
                  {selectedDocument.status === 'SIGNED' && (
                    <Button size="sm" onClick={() => handleUpdateStatus(selectedDocument.legal_document_id, 'SENT')}>
                      <Send className="w-4 h-4 mr-1" /> {language === 'fr' ? 'Envoyer' : 'Send'}
                    </Button>
                  )}
                  {selectedDocument.status === 'SENT' && (
                    <Button size="sm" variant="default" className="bg-green-600 hover:bg-green-700" onClick={() => handleUpdateStatus(selectedDocument.legal_document_id, 'EXECUTED')}>
                      <CheckCircle className="w-4 h-4 mr-1" /> {language === 'fr' ? 'Exécuté' : 'Executed'}
                    </Button>
                  )}
                </div>

                <Separator />

                {/* Target Info */}
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    {language === 'fr' ? 'Personne concernée' : 'Target Person'}
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">{language === 'fr' ? 'Nom' : 'Name'}</p>
                      <p className="font-medium">{selectedDocument.target_name}</p>
                    </div>
                    {selectedDocument.target_national_id && (
                      <div>
                        <p className="text-muted-foreground">{language === 'fr' ? 'ID National' : 'National ID'}</p>
                        <p className="font-mono">{selectedDocument.target_national_id}</p>
                      </div>
                    )}
                    {selectedDocument.target_address && (
                      <div className="col-span-2">
                        <p className="text-muted-foreground">{language === 'fr' ? 'Adresse' : 'Address'}</p>
                        <p>{selectedDocument.target_address}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Execution Info */}
                {(selectedDocument.execution_location || selectedDocument.execution_deadline) && (
                  <>
                    <Separator />
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        {language === 'fr' ? 'Exécution' : 'Execution'}
                      </h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {selectedDocument.execution_location && (
                          <div>
                            <p className="text-muted-foreground">{language === 'fr' ? 'Lieu' : 'Location'}</p>
                            <p>{selectedDocument.execution_location}</p>
                          </div>
                        )}
                        {selectedDocument.execution_deadline && (
                          <div>
                            <p className="text-muted-foreground">{language === 'fr' ? 'Date limite' : 'Deadline'}</p>
                            <p>{new Date(selectedDocument.execution_deadline).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Legal Content */}
                {(selectedDocument.legal_basis || selectedDocument.motives) && (
                  <>
                    <Separator />
                    <div className="space-y-4">
                      {selectedDocument.legal_basis && (
                        <div>
                          <h4 className="font-medium mb-2">{language === 'fr' ? 'Base légale' : 'Legal Basis'}</h4>
                          <p className="text-sm text-muted-foreground bg-muted p-3 rounded">{selectedDocument.legal_basis}</p>
                        </div>
                      )}
                      {selectedDocument.motives && (
                        <div>
                          <h4 className="font-medium mb-2">{language === 'fr' ? 'Motifs' : 'Motives'}</h4>
                          <p className="text-sm text-muted-foreground bg-muted p-3 rounded">{selectedDocument.motives}</p>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Authority */}
                <Separator />
                <div className="flex justify-between items-end text-sm">
                  <div>
                    <p className="text-muted-foreground">{language === 'fr' ? 'Autorité émettrice' : 'Issuing Authority'}</p>
                    <p className="font-medium">{selectedDocument.issuing_authority}</p>
                    {selectedDocument.issuing_magistrate && (
                      <p className="text-muted-foreground">{selectedDocument.issuing_magistrate}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-muted-foreground">{language === 'fr' ? 'Créé le' : 'Created'}</p>
                    <p>{new Date(selectedDocument.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}</p>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                  {language === 'fr' ? 'Fermer' : 'Close'}
                </Button>
                <Button variant="outline">
                  <Printer className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Imprimer' : 'Print'}
                </Button>
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  PDF
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default LegalDocumentsPage;
